import {Component, EventEmitter, Input, Output, inject} from '@angular/core';
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {NgOptimizedImage} from "@angular/common";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {DocumentService} from "../../../shared/services/document.service";
import {DialogUtility} from "@syncfusion/ej2-popups";
import {Dialog} from "@syncfusion/ej2-angular-popups";
import {LanguageService} from "../../../shared/services/language.service";
import {FileStorageResource} from '../../../shared/types/settings-types/fileStorageResource';

@Component({
    selector: 'app-item-documents-preview-selected',
    imports: [
        IconBadgeComponent,
        NgOptimizedImage,
        TooltipComponent
    ],
    templateUrl: './item-documents-preview-selected.component.html',
    styleUrl: './item-documents-preview-selected.component.scss'
})
export class ItemDocumentsPreviewSelectedComponent {
  public documentService: DocumentService = inject(DocumentService);
  private languageService: LanguageService = inject(LanguageService);


  @Input() public file: FileStorageResource | null = null;
  @Input() public files: FileStorageResource[] = [];
  @Input() public position: number = 0;
  @Input() public removeBadge: 'trash' | 'unselect' = 'trash';
  @Output() public  filesChange: EventEmitter<FileStorageResource[]> = new EventEmitter<FileStorageResource[]>();

  private dialogInstance: Dialog | null = null;
  private dialogTexts:{confirm: string, sure: string, okay: string} = {
    confirm: '',
    sure: '',
    okay: '',
  }

  public openGallery(document: FileStorageResource): void {
    this.documentService.showGallery(this.files, document);
  }

  public moveFile(fileToMove: FileStorageResource, direction: 'up' | 'down'): void {
    const index = this.getFileIndex(fileToMove);
    if (index < 0) {
      return;
    }

    const file = this.files[index];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= this.files.length) {
      return;
    }

    this.files[index] = this.files[newIndex];
    this.files[newIndex] = file;
    this.filesChange.emit([...this.files]);
  }

  public showDeleteOverlay(fileToDelete: FileStorageResource): void {
    this.setDialogTexts();
    this.dialogInstance = DialogUtility.confirm({
      title: '<h6 class="text-danger fw-bold m-0 pt-1">' + this.dialogTexts.confirm + '</h6>',
      content: this.dialogTexts.sure,
      cancelButton: {
        text: this.languageService.getTranslationByKey('CANCEL'),
        click: () => this.showDeleteOverlayCancel()
      },
      okButton: {
        text: this.dialogTexts.okay,
        click: () => this.showDeleteOverlayConfirmed(fileToDelete),
        cssClass: 'bg-danger brd-danger'
      },
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }

  private showDeleteOverlayConfirmed(fileToDelete: FileStorageResource): void {
    const index = this.getFileIndex(fileToDelete);
    if (index < 0) {
      this.dialogInstance?.hide();
      return;
    }

    this.files.splice(index, 1);
    this.filesChange.emit([...this.files]);
    this.dialogInstance?.hide();
  }

  private showDeleteOverlayCancel(): void {
    this.dialogInstance?.hide();
  }

  private setDialogTexts(): void {
    if(this.removeBadge === 'trash') {
      this.dialogTexts.confirm = this.languageService.getTranslationByKey('CONFIRMITEMDELETE');
      this.dialogTexts.sure = this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTODELETE');
      this.dialogTexts.okay = this.languageService.getTranslationByKey('DELETE');
    } else {
      this.dialogTexts.confirm = this.languageService.getTranslationByKey('CONFIRMITEMUNSELECT');
      this.dialogTexts.sure = this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTOUNSELECT');
      this.dialogTexts.okay = this.languageService.getTranslationByKey('UNSELECT');
    }
  }

  private getFileIndex(file: FileStorageResource): number {
    const id = file.id;
    if (id) {
      const byId = this.files.findIndex((item) => item.id === id);
      if (byId >= 0) {
        return byId;
      }
    }

    return this.files.indexOf(file);
  }
}
